import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, Circle, XCircle, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useSubscription, PLAN_LABELS, type PlanType } from "@/lib/subscriptionContext";
import { toast } from "sonner";

type StepStatus = "idle" | "running" | "passed" | "failed";
// Starter tier removed — only Pro and Studio (business) are purchasable.
type PlanKey = "pro" | "business";

interface StepState {
  status: StepStatus;
  detail?: string;
}

interface ChecklistState {
  selectedPlan: PlanKey;
  billing: "monthly" | "yearly";
  lastSessionId: string | null;
  lastCancelledAt: string | null;
  steps: Record<string, StepState>;
}

const STORAGE_KEY = "stripe-flow-checklist-state";

const defaultState: ChecklistState = {
  selectedPlan: "pro",
  billing: "monthly",
  lastSessionId: null,
  lastCancelledAt: null,
  steps: {
    click: { status: "idle" },
    success: { status: "idle" },
    failure: { status: "idle" },
    refresh: { status: "idle" },
  },
};

function loadState(): ChecklistState {
  if (typeof window === "undefined") return defaultState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    return { ...defaultState, ...parsed, steps: { ...defaultState.steps, ...(parsed.steps ?? {}) } };
  } catch {
    return defaultState;
  }
}

function saveState(state: ChecklistState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function StatusIcon({ status }: { status: StepStatus }) {
  if (status === "running") return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
  if (status === "passed") return <CheckCircle2 className="h-5 w-5 text-green-600" />;
  if (status === "failed") return <XCircle className="h-5 w-5 text-destructive" />;
  return <Circle className="h-5 w-5 text-muted-foreground" />;
}

export default function StripeFlowChecklist() {
  const { user } = useAuth();
  const { subscription, refreshSubscription } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<ChecklistState>(() => loadState());

  useEffect(() => saveState(state), [state]);

  // If we returned from a Stripe redirect, capture the session_id and verify it.
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const upgrade = searchParams.get("upgrade");
    const cancelled = searchParams.get("cancelled");

    if (cancelled === "true") {
      setState((s) => ({
        ...s,
        lastCancelledAt: new Date().toISOString(),
        steps: { ...s.steps, failure: { status: "passed", detail: "Stripe cancel_url returned correctly." } },
      }));
      const next = new URLSearchParams(searchParams);
      next.delete("cancelled");
      setSearchParams(next, { replace: true });
      return;
    }

    if (sessionId && upgrade === "success") {
      setState((s) => ({
        ...s,
        lastSessionId: sessionId,
        steps: { ...s.steps, success: { status: "running", detail: "Verifying session…" } },
      }));
      (async () => {
        const { data, error } = await supabase.functions.invoke("verify-checkout-session", {
          body: { session_id: sessionId },
        });
        if (error || !data?.ok) {
          setState((s) => ({
            ...s,
            steps: {
              ...s.steps,
              success: {
                status: "failed",
                detail: data?.error || error?.message || "Verification failed",
              },
            },
          }));
        } else {
          setState((s) => ({
            ...s,
            steps: {
              ...s.steps,
              success: {
                status: "passed",
                detail: `Verified plan=${data.plan} status=${data.payment_status} amount=${(data.amount_total ?? 0) / 100} ${(data.currency ?? "usd").toUpperCase()}`,
              },
            },
          }));
          await refreshSubscription();
        }
      })();
      const next = new URLSearchParams(searchParams);
      next.delete("session_id");
      next.delete("upgrade");
      next.delete("plan");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshSubscription]);

  const setStep = (key: string, patch: Partial<StepState>) => {
    setState((s) => ({ ...s, steps: { ...s.steps, [key]: { ...s.steps[key], ...patch } } }));
  };

  const checklistOrigin = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/stripe-flow-checklist`;
  }, []);

  const startCheckout = async (mode: "success" | "failure") => {
    if (!user) {
      toast.error("Sign in first");
      return;
    }
    const stepKey = mode === "success" ? "click" : "failure";
    setStep(stepKey, { status: "running", detail: `Sending plan=${state.selectedPlan} billing=${state.billing}…` });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          plan: state.selectedPlan,
          billing: state.billing,
          // Make Stripe redirect back to THIS page so the checklist can verify.
          origin: window.location.origin,
          success_url: `${checklistOrigin}?upgrade=success&plan=${state.selectedPlan}&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${checklistOrigin}?cancelled=true`,
        },
      });
      if (error || !data?.url) {
        setStep(stepKey, { status: "failed", detail: error?.message || "No checkout URL returned" });
        toast.error("Could not create checkout session");
        return;
      }
      setStep("click", {
        status: "passed",
        detail: `Checkout session created. plan sent: "${state.selectedPlan}"`,
      });
      // Redirect to Stripe.
      window.location.href = data.url;
    } catch (err) {
      setStep(stepKey, {
        status: "failed",
        detail: err instanceof Error ? err.message : "Unknown error",
      });
    }
  };

  const runRefreshCheck = async () => {
    setStep("refresh", { status: "running", detail: "Reloading subscription from backend…" });
    await refreshSubscription();
    // Read fresh value after refresh.
    const { data } = await supabase
      .from("subscriptions")
      .select("plan,status,current_period_end")
      .eq("user_id", user?.id ?? "")
      .maybeSingle();
    if (!data) {
      setStep("refresh", { status: "failed", detail: "No subscription row found" });
      return;
    }
    const matches = data.plan === state.selectedPlan;
    setStep("refresh", {
      status: matches ? "passed" : "failed",
      detail: `Backend plan=${data.plan} status=${data.status}${matches ? "" : ` (expected ${state.selectedPlan})`}`,
    });
  };

  const resetAll = () => {
    setState(defaultState);
    toast.success("Checklist reset");
  };

  const planButtons: { key: PlanKey; label: string }[] = [
    { key: "pro", label: "Pro" },
    { key: "business", label: "Studio" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Stripe payment flow checklist</h1>
            <p className="text-sm text-muted-foreground">
              Guided test for plan click → success redirect → failure redirect → dashboard plan refresh.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={resetAll}>
            <RefreshCw className="mr-2 h-4 w-4" /> Reset
          </Button>
        </div>

        {/* Current state */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Current state</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">User</span>
              <span className="font-mono">{user?.email ?? "— (not signed in)"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Backend plan</span>
              <Badge variant="secondary">
                {subscription ? PLAN_LABELS[subscription.plan as PlanType] : "—"}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last session id</span>
              <span className="truncate font-mono text-xs">{state.lastSessionId ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last cancel at</span>
              <span className="font-mono text-xs">{state.lastCancelledAt ?? "—"}</span>
            </div>
          </CardContent>
        </Card>

        {/* Plan selector */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">1. Pick the plan to test</CardTitle>
            <CardDescription>This is the value sent as <code className="font-mono">plan</code> to create-checkout.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {planButtons.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={state.selectedPlan === p.key ? "default" : "outline"}
                  onClick={() => setState((s) => ({ ...s, selectedPlan: p.key }))}
                >
                  {p.label} <span className="ml-2 text-xs opacity-70">({p.key})</span>
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              {(["monthly", "yearly"] as const).map((b) => (
                <Button
                  key={b}
                  size="sm"
                  variant={state.billing === b ? "default" : "outline"}
                  onClick={() => setState((s) => ({ ...s, billing: b }))}
                >
                  {b}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Steps */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">2. Run the checks</CardTitle>
            <CardDescription>Each step records its result so you can resume after the Stripe redirect.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Step A: click → checkout */}
            <div className="flex items-start gap-3">
              <StatusIcon status={state.steps.click.status} />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">A. Plan click triggers Stripe Checkout</p>
                  <Button size="sm" onClick={() => startCheckout("success")} disabled={!user}>
                    Run <ExternalLink className="ml-1 h-3 w-3" />
                  </Button>
                </div>
                {state.steps.click.detail && (
                  <p className="mt-1 text-xs text-muted-foreground">{state.steps.click.detail}</p>
                )}
              </div>
            </div>

            <Separator />

            {/* Step B: success redirect */}
            <div className="flex items-start gap-3">
              <StatusIcon status={state.steps.success.status} />
              <div className="flex-1">
                <p className="font-medium">B. Success redirect verifies session</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Complete payment in Stripe (use test card <code className="font-mono">4242 4242 4242 4242</code>). You'll be sent back here and the session is auto-verified.
                </p>
                {state.steps.success.detail && (
                  <p className="mt-1 text-xs">{state.steps.success.detail}</p>
                )}
              </div>
            </div>

            <Separator />

            {/* Step C: failure / cancel redirect */}
            <div className="flex items-start gap-3">
              <StatusIcon status={state.steps.failure.status} />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">C. Cancel redirect handled gracefully</p>
                  <Button size="sm" variant="outline" onClick={() => startCheckout("failure")} disabled={!user}>
                    Run, then click "Back" in Stripe
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Open the same checkout, then hit Stripe's back arrow. We expect to land on this page with <code className="font-mono">?cancelled=true</code>.
                </p>
                {state.steps.failure.detail && (
                  <p className="mt-1 text-xs">{state.steps.failure.detail}</p>
                )}
              </div>
            </div>

            <Separator />

            {/* Step D: dashboard plan refresh */}
            <div className="flex items-start gap-3">
              <StatusIcon status={state.steps.refresh.status} />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">D. Dashboard plan refresh matches selected plan</p>
                  <Button size="sm" variant="outline" onClick={runRefreshCheck} disabled={!user}>
                    Run
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Reloads the subscription from the backend and compares it to <code className="font-mono">{state.selectedPlan}</code>.
                </p>
                {state.steps.refresh.detail && (
                  <p className="mt-1 text-xs">{state.steps.refresh.detail}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex gap-3 text-sm">
          <Link to="/dashboard" className="text-primary underline">Open Dashboard</Link>
          <Link to="/pricing" className="text-primary underline">Open Pricing</Link>
        </div>
      </div>
    </div>
  );
}